import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Users, BarChart3, DollarSign, Building2, Cpu, ShieldCheck, TrendingUp, Package, Crown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [statsRes, usersRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
      }
    } finally { setLoading(false); }
  };

  const changeRole = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role }, { withCredentials: true });
      toast.success(`Role updated to ${role}`);
      fetchAll();
    } catch { toast.error('Failed to update role'); }
  };

  const changePlan = async (userId, plan) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/plan`, { plan }, { withCredentials: true });
      toast.success(`Plan updated to ${plan}`);
      fetchAll();
    } catch { toast.error('Failed to update plan'); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading"><div className="mono-data text-lg animate-pulse-red">Loading Admin...</div></div>;
  }

  if (!stats) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-denied">
        <div className="text-center">
          <ShieldCheck className="w-12 h-12 text-[#09090B] mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Access Denied</h2>
          <p className="text-zinc-600">Admin privileges required.</p>
        </div>
      </div>
    );
  }

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users' },
    { id: 'analytics', label: 'Analytics' }
  ];

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      {/* Header */}
      <div className="border-b border-[#09090B] py-8">
        <div className="container-swiss flex items-center justify-between">
          <div>
            <p className="mono-data text-[#09090B] mb-1">Admin Panel</p>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="admin-heading">
              Platform Management
            </h1>
          </div>
          <div className="mono-data text-xs text-zinc-500 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#09090B]" /> {user?.email}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#09090B]">
        <div className="container-swiss flex gap-0">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium tracking-wide uppercase border-r border-[#09090B] transition-colors ${activeTab === tab.id ? 'bg-[#09090B] text-white' : 'hover:bg-zinc-100'}`}
              data-testid={`tab-${tab.id}`}>{tab.label}</button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="container-swiss py-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-0 border-l border-t border-[#09090B] mb-8">
            {[
              { label: 'Total Users', value: stats.total_users, icon: Users },
              { label: 'Active (7d)', value: stats.active_users_7d, icon: TrendingUp },
              { label: 'Pro Users', value: stats.pro_users, icon: Crown },
              { label: 'Total Attempts', value: stats.total_attempts, icon: BarChart3 },
              { label: 'Programs', value: stats.total_programs, icon: Package },
              { label: 'Marketplace', value: stats.marketplace_programs, icon: DollarSign },
              { label: 'Organizations', value: stats.total_orgs, icon: Building2 },
              { label: 'AI Sessions', value: stats.ai_generated_sessions, icon: Cpu },
              { label: 'Revenue', value: `$${stats.platform_revenue}`, icon: DollarSign },
              { label: 'Free Users', value: stats.free_users, icon: Users },
            ].map(s => (
              <div key={s.label} className="grid-border-item" data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="label-swiss mb-2 flex items-center gap-1"><s.icon className="w-3 h-3" /> {s.label}</div>
                <div className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Daily Chart */}
          {analytics?.daily_attempts && (
            <div className="border border-[#09090B] p-6" data-testid="daily-chart">
              <h3 className="mono-data text-sm mb-4">Daily Attempts (14 days)</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={analytics.daily_attempts}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="attempts" fill="#09090B" name="Attempts" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="container-swiss py-8">
          <h3 className="mono-data text-sm mb-4">All Users ({users.length})</h3>
          <div className="border border-[#09090B] overflow-x-auto" data-testid="users-table">
            <div className="grid grid-cols-7 gap-0 border-b border-[#09090B] bg-zinc-50 min-w-[800px]">
              {['Name', 'Email', 'Role', 'Plan', 'Attempts', 'Joined', 'Actions'].map(h => (
                <div key={h} className="p-3 mono-data text-xs">{h}</div>
              ))}
            </div>
            {users.map(u => (
              <div key={u.user_id} className="grid grid-cols-7 gap-0 border-b border-zinc-200 min-w-[800px] last:border-0" data-testid={`user-row-${u.user_id}`}>
                <div className="p-3 text-sm font-medium truncate">{u.name || '-'}</div>
                <div className="p-3 text-xs text-zinc-500 truncate">{u.email}</div>
                <div className="p-3">
                  <select value={u.role} onChange={e => changeRole(u.user_id, e.target.value)}
                    className="text-xs border border-zinc-300 px-2 py-1 bg-white" data-testid={`role-select-${u.user_id}`}>
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                    <option value="creator">creator</option>
                  </select>
                </div>
                <div className="p-3">
                  <select value={u.plan} onChange={e => changePlan(u.user_id, e.target.value)}
                    className="text-xs border border-zinc-300 px-2 py-1 bg-white" data-testid={`plan-select-${u.user_id}`}>
                    <option value="free">free</option>
                    <option value="pro">pro</option>
                    <option value="enterprise">enterprise</option>
                  </select>
                </div>
                <div className="p-3 mono-data text-sm">{u.attempts}</div>
                <div className="p-3 mono-data text-xs text-zinc-400">{u.created_at?.slice(0, 10)}</div>
                <div className="p-3 mono-data text-xs text-zinc-400">{u.user_id.slice(0, 12)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="container-swiss py-8">
          <h3 className="mono-data text-sm mb-4">Top Sessions by Attempts</h3>
          {analytics?.top_sessions?.length > 0 ? (
            <div className="border border-[#09090B]" data-testid="top-sessions">
              {analytics.top_sessions.map((s, i) => (
                <div key={s._id || i} className="flex items-center justify-between p-4 border-b border-zinc-200 last:border-0">
                  <div className="flex items-center gap-4">
                    <span className="mono-data text-xl font-black text-zinc-300 w-8">{String(i + 1).padStart(2, '0')}</span>
                    <span className="text-sm font-medium">{s._id}</span>
                  </div>
                  <span className="mono-data text-sm font-bold">{s.count} attempts</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-sm">No session data yet.</p>
          )}
        </div>
      )}
    </div>
  );
}
